/**
 * PurgeHistoryTab — Wave 6.3 audit log viewer.
 *
 * Searchable table from `GET /v3/super-admin/records/purge-history`. Columns
 * per the brief: PurgedAt, PurgedBy (humanized via Users), EntityType,
 * EntityKey-humanized, ReasonCode (humanized), Reason, "View JSON" button.
 */

import { useEffect, useState } from 'react';
import { Banner, Button, ModalShell, Pill } from '@/components/primitives';
import { useRecordsApi } from '../hooks/useRecordsApi';
import type { PurgeHistoryRow } from '@retail-ops/shared-types';

export function PurgeHistoryTab(): JSX.Element {
  const { client, makeSignal } = useRecordsApi();
  const [rows, setRows] = useState<ReadonlyArray<PurgeHistoryRow>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');
  const [snapshotRow, setSnapshotRow] = useState<PurgeHistoryRow | null>(null);

  async function load(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const res = await client.retail.superAdmin.records.purgeHistory({}, { signal: makeSignal() });
      const anyRes = res as unknown as { success?: boolean; data?: PurgeHistoryRow[]; error?: string };
      if (anyRes.success === false) {
        setError(anyRes.error ?? 'Failed to load purge history.');
        return;
      }
      setRows(anyRes.data ?? []);
    } catch (e: unknown) {
      if (e instanceof Error && e.name === 'AbortError') return;
      setError(e instanceof Error ? e.message : 'Failed to load purge history.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (r.entityKeyHumanized ?? r.entityKey).toLowerCase().includes(q) ||
      (r.purgedByDisplayName ?? '').toLowerCase().includes(q) ||
      r.reason.toLowerCase().includes(q) ||
      (r.reasonCodeLabel ?? r.reasonCode).toLowerCase().includes(q)
    );
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
        <input
          aria-label="Search purge history"
          title="Search purge history"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search reason, actor, or entity"
          style={{
            flex: 1,
            padding: 'var(--sp-3) var(--sp-4)',
            border: '1px solid var(--ink-300)',
            borderRadius: 'var(--r-md)',
            fontSize: 13,
            maxWidth: 480,
          }}
        />
        <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? 'Loading…' : '⟳ Refresh'}
        </Button>
      </div>

      {error && <Banner tone="err">{error}</Banner>}

      <div style={{ background: 'var(--surface-card)', border: '1px solid var(--surface-border)', borderRadius: 'var(--r-md)', overflow: 'auto', maxHeight: '70vh' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--ink-100)', textAlign: 'left' }}>
              <th style={th()}>Purged at</th>
              <th style={th()}>Purged by</th>
              <th style={th()}>Type</th>
              <th style={th()}>Record</th>
              <th style={th()}>Reason</th>
              <th style={th()}>Justification</th>
              <th style={th()}>Snapshot</th>
            </tr>
          </thead>
          <tbody>
            {!loading && visible.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 'var(--sp-6)', textAlign: 'center', color: 'var(--ink-600)' }}>
                  No purge events match.
                </td>
              </tr>
            )}
            {visible.map((r) => (
              <tr key={r.purgeHistoryId} style={{ borderBottom: '1px solid var(--surface-border)' }}>
                <td style={td({ whiteSpace: 'nowrap' })}>{formatDateTime(r.purgedAt)}</td>
                <td style={td()}>{r.purgedByDisplayName ?? r.purgedByOfficerCode ?? '—'}</td>
                <td style={td()}><Pill variant="muted">{r.entityType}</Pill></td>
                <td style={td()}>{r.entityKeyHumanized ?? r.entityKey}</td>
                <td style={td()}>
                  <Pill variant="warn">{r.reasonCodeLabel ?? r.reasonCode}</Pill>
                </td>
                <td style={td({ maxWidth: 320 })}>
                  <span title={r.reason} style={{ display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.reason}
                  </span>
                </td>
                <td style={td()}>
                  {r.snapshotPath ? (
                    <Button size="sm" variant="ghost" onClick={() => setSnapshotRow(r)}>
                      View JSON
                    </Button>
                  ) : (
                    <span style={{ color: 'var(--ink-500)' }}>—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {snapshotRow && (
        <ModalShell
          open
          size="lg"
          title={`Snapshot — ${snapshotRow.entityType} ${snapshotRow.entityKeyHumanized ?? snapshotRow.entityKey}`}
          onClose={() => setSnapshotRow(null)}
          footer={<Button variant="primary" onClick={() => setSnapshotRow(null)}>Close</Button>}
        >
          <div style={{ fontSize: 12, color: 'var(--ink-700)' }}>
            <p>Snapshot path: <code>{snapshotRow.snapshotPath}</code></p>
            <p>To recover this record, switch to the <strong>Recovery</strong> tab and search by purge date or actor.</p>
          </div>
        </ModalShell>
      )}
    </div>
  );
}

function th(): React.CSSProperties {
  return {
    padding: '10px 12px',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    color: 'var(--ink-600)',
    borderBottom: '1px solid var(--surface-border)',
    whiteSpace: 'nowrap',
  };
}

function td(extra?: React.CSSProperties): React.CSSProperties {
  return { padding: '10px 12px', verticalAlign: 'top', color: 'var(--ink-900)', ...extra };
}

function formatDateTime(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
